import { Button } from "../ui/Button";
import { Drawer } from "../ui/Overlay";
import { StatusTag, Tag } from "../ui/Tag";
import { useT } from "../../lib/i18n";
import type { Version } from "../../lib/version";
import { RefreshModeTag } from "./RefreshModePicker";
import { VersionPeople } from "./VersionPeople";

const th = "border-b border-line-lighter bg-fill-light px-3 py-1.5 text-left text-xs font-medium text-ink-secondary";
const td = "border-b border-line-lighter px-3 py-1.5 text-sm text-ink-regular";

/** 版本历史。回滚按钮对生效版本禁用——回滚到自己没有意义 */
export function VersionHistory({
  versions, currentId, hasOnline = true, onClose, onView, onCompare, onRollback, onActivate, onPerson, onLog,
}: {
  /** 点人名：修改记录「全部版本 · 只看他」 */
  onPerson?: (name: string) => void;
  /** 已发布、没在生效的版本「设为生效」：打开 ActivateDialog 选生效方式 */
  onActivate?: (v: Version) => void;
  onLog?: (v: Version) => void;
  versions: Version[];
  currentId: string;
  /** 没有生效版本时「与线上对比」无意义，隐藏 */
  hasOnline?: boolean;
  onClose: () => void;
  onView: (v: Version) => void;
  onCompare: (v: Version) => void;
  onRollback: (v: Version) => void;
}) {
  const t = useT();
  const heads = ["ver.h.id", "ver.h.status", "ver.h.time", "ver.h.people", "ver.h.note", "ver.h.from", "ver.h.refreshMode", "ver.h.actions"] as const;
  return (
    <Drawer title={t("ver.history")} width={1000} onClose={onClose}>
      <table className="w-full border-collapse">
        <thead>
          <tr>{heads.map((h) => <th key={h} className={th}>{t(h)}</th>)}</tr>
        </thead>
        <tbody>
          {versions.map((v) => (
            <tr key={v.id} className={v.status === "active" ? "bg-success-50" : v.id === currentId ? "bg-primary-50" : "bg-white"}>
              <td className={`${td} font-semibold`}>
                {v.id}
                {v.id === currentId && (
                  <span className="font-normal text-ink-placeholder"> · {t(v.status === "draft" ? "ver.h.editing" : "ver.h.viewing")}</span>
                )}
              </td>
              <td className={td}><StatusTag status={v.status} /></td>
              <td className={`${td} whitespace-nowrap text-ink-secondary`}>{v.time}</td>
              <td className={`${td} max-w-[260px]`}><VersionPeople v={v} max={2} onPerson={onPerson} /></td>
              <td className={td}>{v.note || <span className="text-ink-placeholder">{t("ver.h.noNote")}</span>}</td>
              <td className={td}>{v.from ? <Tag tone="warning">{t("ver.rollbackFrom", { id: v.from })}</Tag> : <span className="text-ink-placeholder">—</span>}</td>
              <td className={td}><RefreshModeTag mode={v.refreshMode} /></td>
              <td className={`${td} whitespace-nowrap`}>
                <div className="flex gap-1.5">
                  {onLog && <Button size="sm" onClick={() => onLog(v)}>{t("ver.versionLog")}</Button>}
                  <Button size="sm" onClick={() => onView(v)}>{t("ver.h.view")}</Button>
                  {v.status === "published" && onActivate && <Button size="sm" tone="primary" onClick={() => onActivate(v)}>{t("ver.h.activate")}</Button>}
                  {hasOnline && <Button size="sm" onClick={() => onCompare(v)}>{t("ver.h.compare")}</Button>}
                  <Button size="sm" tone="danger" disabled={v.status === "active"} onClick={() => onRollback(v)}>{t("ver.h.rollback")}</Button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Drawer>
  );
}
